import debounce from 'lodash/debounce';
import React, { useCallback, useEffect } from 'react';

import styled, { css } from 'styled-components';
import { RejectIcon, SearchIcon } from '../Icon';

const InputWrapper = styled.div<{ $hasError?: boolean, $isDisabled?: boolean, $isBorderlessStyle?: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 0;
  font-size: var(--font-size-default);
  line-height: 1;
  background-color: var(--color-input-background);
  color: var(--color-text-default);
  ${({ $isBorderlessStyle }) => !$isBorderlessStyle && css`
    height: var(--input-height-default);
    border: var(--border-input);
    border-radius: var(--border-radius-input);
    padding: 0 6px;

    &:focus-within {
      outline: var(--outline-input-focus);
    }
  `}
  ${({ $isBorderlessStyle }) => $isBorderlessStyle && css`
    height: calc(var(--input-height-default) - 2px);
  `}
  ${({ $isDisabled }) => $isDisabled && css`
    background-color: var(--color-background-disabled);
    border-color: var(--color-input-border-disabled);
  `}
  ${({ $hasError }) => $hasError && css`
    &:not(:focus-within) {
      color: var(--color-error);
      border-color: var(--color-error);
    }
  `}
`;

const Input = styled.input`
  width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
  background-color: inherit;

  &::placeholder {
    color: var(--color-input-placeholder);
    opacity: 1;
  }
`;

const IconWrapper = styled.span`
  margin-left: 5px;
`;

type Props = {
  $isBorderlessStyle?: boolean;
  placeholder?: string;
  value: string;
  isLoading: boolean;
  hasError?: boolean;
  disabled?: boolean;
  autoFocus?: boolean,
  inputId?: string,
  onValueChange: (value: string) => void;
  onKeyDown?: (event: any) => void;
  onFocus?: (event: any) => void;
  onBlur?: (event: any) => void;
  onCancel: () => void;
}

const SearchInput: React.FC<Props> = ({
  $isBorderlessStyle,
  placeholder,
  value,
  isLoading,
  hasError,
  disabled,
  autoFocus,
  inputId,
  onValueChange,
  onKeyDown,
  onFocus,
  onBlur,
  onCancel,
}) => {
  let onKeyDownHandler = onKeyDown;
  if (!onKeyDownHandler) {
    onKeyDownHandler = (event: any): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
  }
  return (
    <InputWrapper $hasError={hasError} $isDisabled={disabled} $isBorderlessStyle={$isBorderlessStyle}>
      <Input
        id={inputId}
        autoFocus={autoFocus}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        onKeyDown={onKeyDownHandler}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <IconWrapper>
        {value
          ? (
            <RejectIcon
              isLoading={isLoading}
              rightMargin={false}
              fixedWidth
              onClick={onCancel}
              data-test="cancel-icon"
            />
          )
          : (
            <SearchIcon
              isLoading={isLoading}
              rightMargin={false}
              fixedWidth
            />
          )}
      </IconWrapper>
    </InputWrapper>
  );
};

type SubmittableSearchInputProps = { onSubmit: (value: string) => void } & Props;

const SubmittableSearchInput: React.FC<SubmittableSearchInputProps> = ({ onSubmit, value, ...rest }) => (
  <form
    style={{ width: '100%' }}
    onSubmit={(event): void => {
      event.preventDefault();
      if (onSubmit) {
        onSubmit(value);
      }
    }}
  >
    <SearchInput value={value} {...rest} />
  </form>
);

type ThrottledSearchInputProps = { onThrottledChange: (value: string) => void } & Props;

const ThrottledSearchInput: React.FC<ThrottledSearchInputProps> = ({
  onThrottledChange, value, onValueChange, onCancel, ...rest
}) => {
  const debouncedCallback = useCallback(
    debounce(onThrottledChange, 250, { leading: false, trailing: true }),
    [onThrottledChange],
  );

  useEffect(() => debouncedCallback.cancel, [debouncedCallback]);

  return (
    <SearchInput
      value={value}
      onValueChange={(val) => {
        onValueChange(val);
        debouncedCallback(val);
      }}
      onCancel={() => {
        onCancel();
        debouncedCallback.cancel();
      }}
      {...rest}
    />
  );
};

export default SearchInput;
export { SubmittableSearchInput, ThrottledSearchInput };
export type { Props, SubmittableSearchInputProps, ThrottledSearchInputProps };
